import { useCallback } from 'react';

import type { MultiSelectOption } from '@rtkwlf/fenrir-react';
import { Field, MultiSelect, Select } from '@rtkwlf/fenrir-react';
import { useTranslation } from 'react-i18next';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';

import { Box, Button } from '@packages/fenrir/components';
import { Stack } from '@packages/fenrir/components/stack';
import { LocationMultiSelectOptionParam } from '@packages/utils/use-query-params/location-multi-select-option-param/location-multi-select-option-param';
import { MultiSelectOptionParam } from '@packages/utils/use-query-params/multi-select-option-param/multi-select-option-param';
import { useLabelLookupMultiSelectOptionParam } from '@packages/utils/use-query-params/use-label-lookup-multi-select-option-param/use-label-lookup-multi-select-option-param';

import { DATA_DIRECTIONS } from './hooks/use-direction-options/use-direction-options';
import { useFlowDataFilterOptions } from './hooks/use-flow-data-filter-options/use-flow-data-filter-options';

export const FlowDataFilters = () => {
  const { t, i18n } = useTranslation('flow');

  const flowDataFilterOptions = useFlowDataFilterOptions();

  const sensorQueryParam = useLabelLookupMultiSelectOptionParam(
    flowDataFilterOptions.sensor,
  );
  const [queryParams, setQueryParams] = useQueryParams({
    device: MultiSelectOptionParam,
    protocol: MultiSelectOptionParam,
    location: LocationMultiSelectOptionParam(i18n),
    site: MultiSelectOptionParam,
    sensor: sensorQueryParam,
    direction: withDefault(StringParam, DATA_DIRECTIONS.BOTH),
  });

  const handleCreate = useCallback(
    (
      newValue: string,
      updateFilterInput: (filterString: string) => void,
      addToOptions: (optionsToAdd: MultiSelectOption[]) => void,
      queryParamKey: 'device' | 'protocol' | 'location' | 'site',
    ) => {
      const newOption = { value: newValue, label: newValue };

      addToOptions([newOption]);
      setQueryParams({
        [queryParamKey]: [...(queryParams[queryParamKey] ?? []), newOption],
      });
      updateFilterInput('');
    },
    [queryParams, setQueryParams],
  );

  const renderPlaceholder = useCallback(
    (options: unknown[] | undefined, placeholderText: string) => {
      if (!options || options.length === 0) {
        return placeholderText;
      }

      return '';
    },
    [],
  );

  return (
    <Stack direction="column">
      <Stack
        direction="row"
        gap={3}
        justifyContent="flex-start"
        flexWrap="wrap"
      >
        <Box sx={{ minWidth: 200, maxWidth: 600 }}>
          <Field.Root>
            <Field.Content width="full">
              <MultiSelect
                options={flowDataFilterOptions.device.options}
                selectedOptions={queryParams.device}
                key="device-filter"
                label={t('filters.device.name')}
                onSelectedOptionsChange={(options) =>
                  setQueryParams({ device: options })
                }
                isCreatable={true}
                handleCreate={(newValue, updateFilterInput) => {
                  handleCreate(
                    newValue,
                    updateFilterInput,
                    flowDataFilterOptions.device.addToCache,
                    'device',
                  );
                }}
                placeholder={renderPlaceholder(
                  queryParams.device,
                  t('filters.device.placeholder'),
                )}
              />
            </Field.Content>
          </Field.Root>
        </Box>

        <Box sx={{ minWidth: 200, maxWidth: 600 }}>
          <Field.Root>
            <Field.Content width="full">
              <MultiSelect
                options={flowDataFilterOptions.protocol.options}
                selectedOptions={queryParams.protocol}
                key="protocol-filter"
                label={t('filters.protocol.name')}
                onSelectedOptionsChange={(options) =>
                  setQueryParams({ protocol: options })
                }
                isCreatable={true}
                handleCreate={(newValue, updateFilterInput) => {
                  handleCreate(
                    newValue,
                    updateFilterInput,
                    flowDataFilterOptions.protocol.addToCache,
                    'protocol',
                  );
                }}
                placeholder={renderPlaceholder(
                  queryParams.protocol,
                  t('filters.protocol.placeholder'),
                )}
              />
            </Field.Content>
          </Field.Root>
        </Box>

        <Box sx={{ minWidth: 200, maxWidth: 600 }}>
          <Field.Root>
            <Field.Content width="full">
              <MultiSelect
                options={flowDataFilterOptions.location.options}
                selectedOptions={queryParams.location}
                key="location-filter"
                label={t('filters.location.name')}
                onSelectedOptionsChange={(options) =>
                  setQueryParams({ location: options })
                }
                placeholder={renderPlaceholder(
                  queryParams.location,
                  t('filters.location.placeholder'),
                )}
              />
            </Field.Content>
          </Field.Root>
        </Box>

        <Box sx={{ minWidth: 200, maxWidth: 600 }}>
          <Field.Root>
            <Field.Content width="full">
              <MultiSelect
                options={flowDataFilterOptions.site.options}
                selectedOptions={queryParams.site}
                key="site-filter"
                label={t('filters.site.name')}
                onSelectedOptionsChange={(options) =>
                  setQueryParams({ site: options })
                }
                isCreatable={true}
                handleCreate={(newValue, updateFilterInput) => {
                  handleCreate(
                    newValue,
                    updateFilterInput,
                    flowDataFilterOptions.site.addToCache,
                    'site',
                  );
                }}
                placeholder={renderPlaceholder(
                  queryParams.site,
                  t('filters.site.placeholder'),
                )}
              />
            </Field.Content>
          </Field.Root>
        </Box>

        <Box sx={{ minWidth: 200, maxWidth: 600 }}>
          <Field.Root>
            <Field.Content>
              <MultiSelect
                options={flowDataFilterOptions.sensor}
                selectedOptions={queryParams.sensor}
                key="sensor-filter"
                label={t('filters.sensor.name')}
                onSelectedOptionsChange={(options) =>
                  setQueryParams({ sensor: options })
                }
                placeholder={renderPlaceholder(
                  queryParams.sensor,
                  t('filters.sensor.placeholder'),
                )}
              />
            </Field.Content>
          </Field.Root>
        </Box>

        <Box sx={{ minWidth: 200, maxWidth: 600 }}>
          <Field.Root>
            <Field.Label htmlFor="direction-filter">
              {t('filters.direction.name')}
            </Field.Label>
            <Field.Content width="full">
              <Select
                key="direction-filter"
                isClearable={false}
                options={flowDataFilterOptions.direction}
                value={queryParams.direction ?? undefined}
                onChange={(value) => setQueryParams({ direction: value })}
              />
            </Field.Content>
          </Field.Root>
        </Box>
      </Stack>
      <Stack direction="row" justifyContent="flex-end">
        <Button
          color="primary"
          variant="outlined"
          disabled={Object.values(queryParams).every(
            (param) => !(param ?? []).length,
          )}
          onClick={() =>
            setQueryParams({
              device: [],
              protocol: [],
              location: [],
              site: [],
              sensor: [],
              direction: DATA_DIRECTIONS.BOTH,
            })
          }
        >
          {t('components:filter.resetAll')}
        </Button>
      </Stack>
    </Stack>
  );
};
